<template>
  <Teleport to="body">
  <div class="modal-overlay">
    <div
      class="mobile-status-container"
      :class="{ 'small-modal': mode !== 'update', 'reactivate-modal': mode === 'reactivate' }"
      @click.stop
    >
      <template v-if="mode === 'update'">
        <div class="modal-header">
          <h1 class="mobile-status-title">Update Mobile Number</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">
              <span class="required">*</span> Mobile number (used to log in)
            </label>
            <div class="mobile-input-wrapper" :class="{ error: hasError }">
              <span class="country-code">+1</span>
              <input
                :value="newMobile"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                class="mobile-input"
                placeholder="Enter new mobile number"
                :maxlength="phoneLimit"
                @input="handleMobileInput"
                @blur="handleBlur"
              />
              <img v-if="hasError" :src="alertIcon" alt="error" class="error-icon-img" />
            </div>
            <span v-if="hasError" class="error-text">{{ errorMessage }}</span>
          </div>
        </div>

        <div class="mobile-status-actions">
          <button class="btn btn-next" :disabled="submitting" @click="handleNext">
            <span v-if="submitting" class="spinner"></span>
            <span v-else>Submit</span>
          </button>
        </div>
      </template>

      <template v-else-if="mode === 'deactivate'">
        <div class="modal-header">
          <h1 class="mobile-status-title">Deactivate User</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body deactivate-body">
          <p class="deactivate-text">
            Are you sure you want to deactivate this account. This user cannot login to HomeMetric.
            You can reactivate it if needed.
          </p>
        </div>

        <div class="mobile-status-actions">
          <button class="btn btn-no" @click="handleClose">No</button>
          <button class="btn btn-yes" :disabled="submitting" @click="handleConfirm">Yes</button>
        </div>
      </template>

      <template v-else-if="mode === 'reactivate'">
        <div class="modal-header reactivate-header">
          <h1 class="mobile-status-title">Reactivate User</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <div class="modal-body deactivate-body">
          <p class="deactivate-text reactivate-text">
            Are you sure you want to reactivate this account? It will remain available.
          </p>
        </div>

        <div class="mobile-status-actions">
          <button class="btn btn-no" @click="handleClose">No</button>
          <button class="btn btn-yes" :disabled="submitting" @click="handleConfirm">Yes</button>
        </div>
      </template>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, inject } from 'vue'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import alertIcon from '@/assets/alert.png'
import { updateMobile } from '@/api/updatemobile'
import { updateTechnicianStatus } from '@/api/updatestatus'
import { getTechnicianDetail } from '@/api/detailTechnicians'
import { sanitizeDigitsOnly } from '@/utils/fieldLimitValidation'

const phoneLimit = 10

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const props = defineProps<{
  technicianId: string
  mode: 'update' | 'deactivate' | 'reactivate'
  phone?: string
  phoneCode?: string
}>()

function extractPhoneNumber(phone: string, phoneCode?: string): string {
  if (!phone) return ''

  let digits = phone.replace(/[^\d]/g, '')
  const code = (phoneCode || '1').replace(/[^\d]/g, '')

  if (code && digits.startsWith(code) && digits.length > 10) {
    digits = digits.slice(code.length)
  } else if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  return digits.length > 10 ? digits.slice(-10) : digits
}

const newMobile = ref('')
const hasError = ref(false)
const errorMessage = ref('')
const submitting = ref(false)

async function loadCurrentPhone() {
  if (props.mode !== 'update') return

  const propPhone = props.phone?.trim()
  if (propPhone) {
    newMobile.value = extractPhoneNumber(propPhone, props.phoneCode)
    return
  }

  if (!props.technicianId) return

  try {
    const response = await getTechnicianDetail(props.technicianId)
    if (response.code === 200 && response.data) {
      const data = response.data
      const rawPhone = data.mobile || data.phone || ''
      newMobile.value = extractPhoneNumber(rawPhone, data.phone_code)
    }
  } catch (error) {
    console.error('Failed to load technician phone:', error)
  }
}

watch(
  () => [props.mode, props.phone, props.phoneCode, props.technicianId] as const,
  () => {
    if (props.mode === 'update') {
      void loadCurrentPhone()
    }
  },
  { immediate: true },
)

function handleClose() {
  emit('close')
}

function handleMobileInput(event: Event) {
  newMobile.value = sanitizeDigitsOnly((event.target as HTMLInputElement).value, phoneLimit)
  hasError.value = false
  errorMessage.value = ''
}

function handleBlur() {
  const phone = newMobile.value.trim()
  const isValidPhone = /^\d{10}$/.test(phone)

  if (phone && !isValidPhone) {
    hasError.value = true
    errorMessage.value = 'Please enter a 10-digit mobile number'
  }
}

function closeDelay() {
  setTimeout(() => emit('close'), 2100)
}

async function handleNext() {
  const phone = newMobile.value.trim()

  // 验证是否为10位数字
  const isValidPhone = /^\d{10}$/.test(phone)

  if (!isValidPhone) {
    hasError.value = true
    errorMessage.value = 'Please enter a 10-digit mobile number'
    showToast('warning', 'Please enter valid phone number')
    return
  }

  hasError.value = false
  errorMessage.value = ''

  hasError.value = false
  submitting.value = true

  try {
    const response = await updateMobile({
      id: Number(props.technicianId),
      phone_code: '1',
      phone: newMobile.value.trim(),
    })

    if (response.code === 200) {
      showToast('success', 'Successfully updated')
      emit('saved')
      closeDelay()
    } else {
      showToast('error', response.msg || 'Mobile number already exist')
    }
  } catch (error) {
    console.error('Update mobile error:', error)
    showToast('error', 'Mobile number already exist')
  } finally {
    submitting.value = false
  }
}

async function handleConfirm() {
  submitting.value = true

  try {
    const status = props.mode === 'deactivate' ? 0 : 1

    const response = await updateTechnicianStatus({
      id: Number(props.technicianId),
      status: status,
    })

    if (response.code === 200) {
      showToast(
        'success',
        props.mode === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated',
      )
      emit('saved')
      closeDelay()
    } else {
      showToast('error', response.msg || 'Operation failed')
    }
  } catch (error) {
    console.error('Status update error:', error)
    showToast('error', 'Network request failed')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.mobile-status-container {
  background: #fff;
  border-radius: 12px;
  width: 530px;
  height: 236px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
.mobile-status-container.small-modal.reactivate-modal {
  width: 530px;
  height: 172px;
}
.mobile-status-container.small-modal {
  width: 530px;
  height: 212px;
}
.mobile-status-container.small-modal .reactivate-header {
  padding-bottom: 24px;
}

.mobile-status-container.small-modal .reactivate-header + .modal-body {
  flex: none;
  padding: 0 24px;
}

.reactivate-modal .deactivate-text,
.reactivate-text {
  white-space: nowrap;
}

.reactivate-modal .mobile-status-actions {
  margin-top: 24px;
  padding: 0 24px 24px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 0 24px;
  flex-shrink: 0;
}

.small-modal .modal-header {
  padding: 24px;
}

.mobile-status-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  text-align: left;
}

.small-modal .mobile-status-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.close-btn {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
}

.small-modal .close-icon {
  width: 16px;
  height: 16px;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.small-modal .modal-body {
  padding-right: 24px;
  padding-left: 24px;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.deactivate-body {
  padding: 0;
}

.deactivate-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
}

.small-modal .deactivate-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.form-group {
  margin-top: -16px;
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.mobile-input-wrapper {
  width: 482px;
  height: 32px;
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  transition: all 0.2s ease;
}

.mobile-input-wrapper:focus-within {
  border-color: #666666;
}

.mobile-input-wrapper.error {
  border-color: #ff4d4f;
}

.country-code {
  position: relative;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  height: 32px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.country-code::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.mobile-input {
  flex: 1;
  padding: 0 16px;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  background: transparent;
  height: 32px;
}

.mobile-input::placeholder {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #cccccc;
}

.error-icon-img {
  width: 16px;
  height: 16px;
  margin-right: 12px;
  object-fit: contain;
  flex-shrink: 0;
}

.error-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #ff4d4f;
}

.mobile-status-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px;
}

.small-modal .mobile-status-actions {
  gap: 8px;
  padding: 0 24px 24px;
}

.btn {
  padding: 10px 24px;
  border-radius: 100px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.small-modal .btn {
  width: 80px;
  height: 32px;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.btn-next {
  width: 80px;
  height: 32px;
  background: #000000;
  color: #fff;
  border-radius: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: none;
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.btn-next:hover:not(:disabled) {
  width: 80px;
  height: 32px;
  background: #333;
}
.btn-next:disabled {
  opacity: 0.7;
}
.btn-next .spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-no {
  width: 80px;
  height: 32px;
  border: 1px solid #000000;
  background: #fff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-no:hover {
  border-color: #000000;
  background-color: #000000;
  color: #ffffff;
}

.btn-yes {
  width: 80px;
  height: 32px;
  border: 1px solid #000000;
  background: #000000;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-yes:hover {
  background: #484848;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-body {
    padding: 16px;
  }
}
</style>
